// src/components/MasterDataTable.jsx
// Component กลางที่ใช้ร่วมกันทั้ง 3 ตาราง
import React, { useEffect, useState } from "react";
import Button1 from "@material-ui/core/Button";
import { useMasterData } from "../../hooks/useMasterData";

// ── Table config per tableKey ─────────────────────────────────────────────────
export const TABLE_CONFIG = {
  inquiry: {
    label:    "Inquiry Master",
    pk:       "INQUIRY_MASTER_ID",
    nameCol:  "INQUIRY_MASTER_NAME",
    valueCol: "INQUIRY_MASTER_VALUE",
    enCol:    "INQUIRY_MASTER_EN",
    thCol:    "INQUIRY_MASTER_TH",
    hasSeq:   true,
    hasActive: true,
    icon:     "ti-help-circle",
  },
  customer: {
    label:    "Customer Master",
    pk:       "CUSTOMER_MASTER_ID",
    nameCol:  "CUSTOMER_MASTER_NAME",
    valueCol: "CUSTOMER_MASTER_VALUE",
    enCol:    "CUSTOMER_MASTER_EN",
    thCol:    "CUSTOMER_MASTER_TH",
    hasSeq:   false,
    hasActive: false,
    icon:     "ti-building",
  },
  campaign: {
    label:    "Campaign Master",
    pk:       "CAMPAIGN_MASTER_ID",
    nameCol:  "CAMPAIGN_MASTER_NAME",
    valueCol: "CAMPAIGN_MASTER_VALUE",
    enCol:    "CAMPAIGN_MASTER_EN",
    thCol:    "CAMPAIGN_MASTER_TH",
    hasSeq:   false,
    hasActive: false,
    icon:     "ti-speakerphone",
  },
};

// ── Input style helper ────────────────────────────────────────────────────────
const inputStyle = (invalid) => ({
  width: "100%", padding: "7px 10px", borderRadius: 6,
  border: `1px solid ${invalid ? "#fca5a5" : "#d1d5db"}`,
  fontSize: 13, color: "#1e293b", fontFamily: "inherit",
  outline: "none", boxSizing: "border-box",
});

const Label = ({ children, required }) => (
  <label style={{ fontWeight: 700, fontSize: 12, color: "#374151", display: "block", marginBottom: 5 }}>
    {children} {required && <span style={{ color: "#dc2626" }}>*</span>}
  </label>
);

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          position: "relative", width: 36, height: 20, borderRadius: 20, flexShrink: 0,
          background: checked ? "#22c55e" : "#cbd5e1", transition: "background 0.2s", cursor: "pointer",
        }}
      >
        <div style={{
          position: "absolute", top: 3, left: checked ? 17 : 3,
          width: 14, height: 14, borderRadius: "50%",
          background: "#fff", transition: "left 0.2s",
        }} />
      </div>
      <span style={{ fontSize: 13, color: checked ? "#16a34a" : "#94a3b8", fontWeight: 600 }}>
        {label ?? (checked ? "Active" : "Inactive")}
      </span>
    </label>
  );
}

// ── Add/Edit Modal ────────────────────────────────────────────────────────────
function MasterForm({ open, cfg, row, onSave, onClose }) {
  const isEdit  = Boolean(row);
  const EMPTY   = {
    [cfg.nameCol]:  "",
    [cfg.valueCol]: "",
    [cfg.enCol]:    "",
    [cfg.thCol]:    "",
    SEQ:       1,
    IS_ACTIVE: true,
  };
  const [form,   setForm]   = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(isEdit ? {
      [cfg.nameCol]:  row[cfg.nameCol]  ?? "",
      [cfg.valueCol]: row[cfg.valueCol] ?? "",
      [cfg.enCol]:    row[cfg.enCol]    ?? "",
      [cfg.thCol]:    row[cfg.thCol]    ?? "",
      SEQ:       row.SEQ       ?? 1,
      IS_ACTIVE: row.IS_ACTIVE ?? true,
    } : EMPTY);
    setErrors({});
  }, [open, row]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form[cfg.nameCol].trim()) e[cfg.nameCol] = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({ ...form, SEQ: Number(form.SEQ) });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1300,
      background: "#0006", display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 12,
        width: "min(540px, 95vw)", maxHeight: "90vh",
        display: "flex", flexDirection: "column", boxShadow: "0 8px 40px #0003",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 24px", borderBottom: "1px solid #e2e8f0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span style={{ fontWeight: 800, fontSize: 15, color: "#1e293b" }}>
            {isEdit ? `✏️ Edit ${cfg.label}` : `➕ Add ${cfg.label}`}
          </span>
          <button onClick={onClose} style={{ background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#64748b" }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>

            {/* Name — full width */}
            <div style={{ gridColumn: "1 / -1" }}>
              <Label required>Name (Key)</Label>
              <input
                style={inputStyle(errors[cfg.nameCol])}
                value={form[cfg.nameCol]}
                onChange={e => set(cfg.nameCol, e.target.value)}
                placeholder="ชื่อ master key"
              />
              {errors[cfg.nameCol] && <span style={{ fontSize: 11, color: "#dc2626" }}>{errors[cfg.nameCol]}</span>}
            </div>

            {/* Value */}
            <div style={{ gridColumn: "1 / -1" }}>
              <Label>Value</Label>
              <input
                style={inputStyle(false)}
                value={form[cfg.valueCol]}
                onChange={e => set(cfg.valueCol, e.target.value)}
                placeholder="ค่าที่ใช้ในระบบ"
              />
            </div>

            {/* EN */}
            <div>
              <Label>Label (EN)</Label>
              <input
                style={inputStyle(false)}
                value={form[cfg.enCol]}
                onChange={e => set(cfg.enCol, e.target.value)}
                placeholder="English label"
              />
            </div>

            {/* TH */}
            <div>
              <Label>Label (TH)</Label>
              <input
                style={inputStyle(false)}
                value={form[cfg.thCol]}
                onChange={e => set(cfg.thCol, e.target.value)}
                placeholder="ป้ายภาษาไทย"
              />
            </div>

            {/* SEQ + IS_ACTIVE (inquiry only) */}
            {cfg.hasSeq && (
              <div>
                <Label>Sequence</Label>
                <input
                  type="number" min="1"
                  style={inputStyle(false)}
                  value={form.SEQ}
                  onChange={e => set("SEQ", e.target.value)}
                />
              </div>
            )}
            {cfg.hasActive && (
              <div style={{ paddingTop: 4 }}>
                <Label>Status</Label>
                <Toggle
                  checked={form.IS_ACTIVE}
                  onChange={v => set("IS_ACTIVE", v)}
                  label={form.IS_ACTIVE ? "Active" : "Inactive"}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 24px", borderTop: "1px solid #e2e8f0",
          display: "flex", justifyContent: "flex-end", gap: 10, background: "#fff",
        }}>
          <Button1 variant="outlined" size="small" onClick={onClose} disabled={saving}
            style={{ textTransform: "none", fontSize: 13 }}>
            ยกเลิก
          </Button1>
          <Button1 variant="contained" color="primary" size="small"
            onClick={handleSave} disabled={saving}
            style={{ textTransform: "none", fontSize: 13, fontWeight: 700 }}>
            {saving ? "⏳ กำลังบันทึก..." : "💾 บันทึก"}
          </Button1>
        </div>
      </div>
    </div>
  );
}

// ── Confirm Delete Modal ──────────────────────────────────────────────────────
function ConfirmDelete({ open, name, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1400,
      background: "#0006", display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, width: "min(400px, 92vw)",
        boxShadow: "0 8px 40px #0003",
      }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 800, fontSize: 15, color: "#1e293b" }}>🗑️ ยืนยันลบ</span>
          <button onClick={onCancel} style={{ background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#64748b" }}>✕</button>
        </div>
        <div style={{ padding: "18px 24px" }}>
          <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.6 }}>
            ยืนยันลบ <strong>"{name}"</strong>?<br/>ไม่สามารถกู้คืนได้
          </p>
        </div>
        <div style={{ padding: "12px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Button1 variant="outlined" size="small" onClick={onCancel} style={{ textTransform:"none",fontSize:13 }}>ยกเลิก</Button1>
          <Button1 variant="contained" size="small" onClick={onConfirm}
            style={{ textTransform:"none",fontSize:13,fontWeight:700,background:"#dc2626",color:"#fff" }}>
            🗑️ ยืนยันลบ
          </Button1>
        </div>
      </div>
    </div>
  );
}

// ── Main table component ──────────────────────────────────────────────────────
export function MasterDataTable({ tableKey, showToast }) {
  const cfg = TABLE_CONFIG[tableKey];
  const { rows, loading, error, clearError, fetchAll, create, update, remove } = useMasterData(tableKey);

  const [search,      setSearch]      = useState("");
  const [formOpen,    setFormOpen]    = useState(false);
  const [editingRow,  setEditingRow]  = useState(null);
  const [confirmId,   setConfirmId]   = useState(null);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = rows.filter(r =>
    [r[cfg.nameCol], r[cfg.valueCol], r[cfg.enCol], r[cfg.thCol]]
      .some(v => v?.toLowerCase?.().includes(search.toLowerCase()))
  );

  const handleSave = async (payload) => {
    try {
      if (editingRow) {
        await update(editingRow.id, payload);
        showToast(`✅ แก้ไข "${payload[cfg.nameCol]}" สำเร็จ`);
      } else {
        await create(payload);
        showToast(`✅ เพิ่ม "${payload[cfg.nameCol]}" สำเร็จ`);
      }
      setFormOpen(false);
      setEditingRow(null);
    } catch (e) {
      showToast("❌ " + e.message, "error");
    }
  };

  const handleDelete = async () => {
    try {
      const row = rows.find(r => r.id === confirmId);
      await remove(confirmId);
      showToast(`🗑️ ลบ "${row?.[cfg.nameCol]}" สำเร็จ`);
      setConfirmId(null);
    } catch (e) {
      showToast("❌ " + e.message, "error");
    }
  };

  const confirmRow = rows.find(r => r.id === confirmId);

  const thStyle = {
    padding: "9px 12px", fontSize: 11, fontWeight: 700,
    color: "#64748b", textTransform: "uppercase",
    textAlign: "left", background: "#f1f5f9",
    borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap",
  };
  const tdStyle = {
    padding: "9px 12px", fontSize: 13, color: "#1e293b",
    borderBottom: "1px solid #f1f5f9", verticalAlign: "middle",
  };

  return (
    <>
      <MasterForm
        open={formOpen}
        cfg={cfg}
        row={editingRow}
        onSave={handleSave}
        onClose={() => { setFormOpen(false); setEditingRow(null); }}
      />
      <ConfirmDelete
        open={confirmId !== null}
        name={confirmRow?.[cfg.nameCol]}
        onConfirm={handleDelete}
        onCancel={() => setConfirmId(null)}
      />

      {/* Toolbar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "12px 16px", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap",
      }}>
        <input
          type="text"
          placeholder={`Search ${cfg.label}...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: "6px 12px", fontSize: 13, borderRadius: 6,
            border: "1px solid #e2e8f0", color: "#1e293b",
            outline: "none", width: 240,
          }}
        />
        <span style={{ fontSize: 12, color: "#94a3b8" }}>
          {filtered.length} รายการ
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Button1 variant="outlined" size="small"
            onClick={fetchAll} disabled={loading}
            style={{ textTransform: "none", fontSize: 12 }}>
            {loading ? "⏳" : "🔄 Refresh"}
          </Button1>
          <Button1 variant="contained" color="primary" size="small"
            onClick={() => { setEditingRow(null); setFormOpen(true); }}
            style={{ textTransform: "none", fontSize: 12, fontWeight: 700 }}>
            ➕ Add
          </Button1>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div onClick={clearError} style={{
          margin: "10px 16px", padding: "8px 12px",
          background: "#fef2f2", color: "#dc2626",
          border: "1px solid #fca5a5", borderRadius: 6,
          fontSize: 12, fontWeight: 600, cursor: "pointer",
        }}>
          ❌ {error} (คลิกเพื่อปิด)
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: "auto", flex: 1 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 60 }}>#</th>
              <th style={thStyle}>Name (Key)</th>
              <th style={thStyle}>Value</th>
              <th style={thStyle}>EN</th>
              <th style={thStyle}>TH</th>
              {cfg.hasSeq    && <th style={{ ...thStyle, width: 70, textAlign: "center" }}>Seq</th>}
              {cfg.hasActive && <th style={{ ...thStyle, width: 80, textAlign: "center" }}>Status</th>}
              <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} style={{ ...tdStyle, textAlign: "center", color: "#94a3b8", padding: 32 }}>
                  ⏳ กำลังโหลด...
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} style={{ ...tdStyle, textAlign: "center", color: "#94a3b8", padding: 32 }}>
                  ไม่พบข้อมูล
                </td>
              </tr>
            )}
            {!loading && filtered.map((row, i) => (
              <tr key={row.id}
                style={{ background: i % 2 === 1 ? "#fafafa" : "#fff" }}
                onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 1 ? "#fafafa" : "#fff"}
              >
                <td style={{ ...tdStyle, color: "#94a3b8", fontSize: 11 }}>{row.id}</td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{row[cfg.nameCol]}</td>
                <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 12, color: "#475569" }}>{row[cfg.valueCol] || "—"}</td>
                <td style={tdStyle}>{row[cfg.enCol] || "—"}</td>
                <td style={tdStyle}>{row[cfg.thCol] || "—"}</td>
                {cfg.hasSeq && (
                  <td style={{ ...tdStyle, textAlign: "center", color: "#64748b" }}>{row.SEQ}</td>
                )}
                {cfg.hasActive && (
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <span style={{
                      fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 700,
                      background: row.IS_ACTIVE ? "#dcfce7" : "#f1f5f9",
                      color:      row.IS_ACTIVE ? "#16a34a" : "#94a3b8",
                    }}>
                      {row.IS_ACTIVE ? "Active" : "Inactive"}
                    </span>
                  </td>
                )}
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                    <button
                      onClick={() => { setEditingRow(row); setFormOpen(true); }}
                      style={{
                        padding: "3px 10px", fontSize: 11, fontWeight: 700,
                        border: "1px solid #e2e8f0", borderRadius: 5,
                        background: "#fff", color: "#1d4ed8", cursor: "pointer",
                      }}>
                      ✏️
                    </button>
                    <button
                      onClick={() => setConfirmId(row.id)}
                      style={{
                        padding: "3px 10px", fontSize: 11, fontWeight: 700,
                        border: "1px solid #fca5a5", borderRadius: 5,
                        background: "#fff", color: "#dc2626", cursor: "pointer",
                      }}>
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
